import React, { useEffect, useMemo, useState } from "react";
import { Badge } from "../../../../ui/components/Badge";
import { Button } from "../../../../ui/components/Button";
import {
  FeatherExternalLink,
  FeatherPaperclip,
  FeatherX,
} from "@subframe/core";
import { MailMessageDTO } from "../../data/api/botMailApi";

interface MessageViewerProps {
  message: MailMessageDTO;
  mailboxEmail: string;
  loading?: boolean;
  onClose: () => void;
}

const formatBytes = (bytes: number | null): string => {
  if (bytes === null) return "—";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const formatWhen = (iso: string | null): string => {
  if (!iso) return "—";
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? "—" : date.toLocaleString();
};

/**
 * Wrap the mail's own HTML so it renders legibly inside the iframe. The mail
 * body is untrusted content from the internet, so it is NOT injected into our
 * DOM — see the sandbox note below.
 */
const wrapHtml = (html: string): string => `<!doctype html>
<html><head><meta charset="utf-8">
<style>
  html,body { margin:0; padding:16px; background:#fff;
    font:14px/1.55 -apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,sans-serif; color:#171717; }
  img,table { max-width:100% !important; height:auto; }
  table { border-collapse:collapse; }
  a { color:#1d4ed8; }
  pre { white-space:pre-wrap; word-break:break-word; }
</style></head><body>${html}</body></html>`;

/**
 * Reading pane for one mail.
 *
 * The HTML body is rendered inside a fully sandboxed iframe (`sandbox=""`, no
 * allow-scripts, no allow-same-origin). Mail bodies are attacker-controlled, so
 * this keeps their scripts, forms and framing away from an admin session that
 * holds a master-admin JWT. Links are inert by design; use "Open in Gmail" to
 * follow anything.
 */
export const MessageViewer: React.FC<MessageViewerProps> = ({
  message,
  mailboxEmail,
  loading = false,
  onClose,
}) => {
  const hasHtml = !!message.body_html;
  const hasText = !!message.body_text;

  /**
   * Mode is DERIVED, not plain state, with an explicit user override on top.
   *
   * The list opens a message by mounting this component with a body-less
   * placeholder (instant feedback) and swapping in the real row a moment later.
   * A `useState(hasHtml ? …)` initialiser only runs on that first mount, when
   * body_html is still null — so it would latch to "text" and never show the
   * HTML once it arrived. Deriving from the current props fixes that while
   * still honouring a deliberate click on "Plain text".
   */
  const [override, setOverride] = useState<"html" | "text" | null>(null);
  const mode: "html" | "text" = override ?? (hasHtml ? "html" : "text");

  // A different message starts fresh — one mail read as text shouldn't force
  // the next one to text as well.
  useEffect(() => {
    setOverride(null);
  }, [message.provider_message_id]);

  const srcDoc = useMemo(
    () => (message.body_html ? wrapHtml(message.body_html) : ""),
    [message.body_html]
  );

  const gmailUrl = `https://mail.google.com/mail/u/?authuser=${encodeURIComponent(
    mailboxEmail
  )}#all/${encodeURIComponent(message.provider_message_id)}`;

  return (
    <div className="flex w-full flex-col items-start overflow-hidden rounded-lg border border-solid border-neutral-border bg-default-background xl:w-auto xl:grow xl:basis-0">
      {/* Header */}
      <div className="flex w-full flex-col items-start gap-3 border-b border-solid border-neutral-border px-6 py-4">
        <div className="flex w-full items-start justify-between gap-4">
          <span className="text-heading-3 font-heading-3 text-default-font">
            {message.subject || "(no subject)"}
          </span>
          <div className="flex shrink-0 items-center gap-2">
            <Button
              variant="neutral-tertiary"
              size="small"
              icon={<FeatherExternalLink />}
              onClick={() => window.open(gmailUrl, "_blank", "noopener,noreferrer")}
            >
              Open in Gmail
            </Button>
            <Button
              variant="neutral-tertiary"
              size="small"
              icon={<FeatherX />}
              onClick={onClose}
            >
              Close
            </Button>
          </div>
        </div>

        <div className="flex w-full flex-wrap items-center gap-x-4 gap-y-1">
          <span className="text-body-bold font-body-bold text-default-font">
            {message.from_name || message.from_email || "Unknown sender"}
          </span>
          {message.from_name && message.from_email && (
            <span className="text-caption font-caption text-subtext-color">
              &lt;{message.from_email}&gt;
            </span>
          )}
          <span className="text-caption font-caption text-subtext-color">
            {formatWhen(message.received_at)}
          </span>
          {message.is_unread && <Badge variant="brand">Unread</Badge>}
          {message.processed_status !== "new" && (
            <Badge variant="neutral">{message.processed_status}</Badge>
          )}
        </div>

        <div className="flex w-full flex-col items-start gap-0.5">
          <span className="text-caption font-caption text-subtext-color">
            To: {message.to_emails || "—"}
          </span>
          {/* With a zoho-*@ catch-all, this is the address that identifies the
              client — To: can say something else entirely. */}
          {message.delivered_to &&
            message.delivered_to.toLowerCase() !== mailboxEmail.toLowerCase() && (
              <span className="text-caption font-caption text-brand-700">
                Delivered to: {message.delivered_to}
              </span>
            )}
          {message.cc_emails && (
            <span className="text-caption font-caption text-subtext-color">
              Cc: {message.cc_emails}
            </span>
          )}
        </div>

        {message.attachments.length > 0 && (
          <div className="flex w-full flex-wrap items-center gap-2">
            {message.attachments.map((attachment, index) => (
              <div
                key={`${attachment.filename}-${index}`}
                className="flex items-center gap-1.5 rounded-md border border-solid border-neutral-border px-2 py-1"
                title={attachment.mime_type || undefined}
              >
                <FeatherPaperclip className="h-3 w-3 text-subtext-color" />
                <span className="text-caption font-caption text-default-font">
                  {attachment.filename || "attachment"}
                </span>
                <span className="text-caption font-caption text-subtext-color">
                  {formatBytes(attachment.size_bytes)}
                </span>
              </div>
            ))}
            {/* Bytes aren't downloaded yet — only the metadata is stored. */}
            <span className="text-caption font-caption text-subtext-color">
              (listed only — not downloaded)
            </span>
          </div>
        )}

        {hasHtml && hasText && (
          <div className="flex items-center gap-2">
            <Button
              variant={mode === "html" ? "neutral-secondary" : "neutral-tertiary"}
              size="small"
              onClick={() => setOverride("html")}
            >
              HTML
            </Button>
            <Button
              variant={mode === "text" ? "neutral-secondary" : "neutral-tertiary"}
              size="small"
              onClick={() => setOverride("text")}
            >
              Plain text
            </Button>
          </div>
        )}
      </div>

      {/*
        Body. Height is explicit (70vh) rather than flex-grown: this card sits in
        a row whose height is content-driven, so `grow basis-0` resolved to 0 and
        an `overflow-auto` wrapper clipped the whole body away. The iframe and
        the <pre> each scroll internally instead.
      */}
      <div className="flex w-full flex-col items-start">
        {loading ? (
          <span className="p-6 text-body font-body text-subtext-color">Loading mail…</span>
        ) : mode === "html" && hasHtml ? (
          <iframe
            title={message.subject || "Mail body"}
            sandbox=""
            srcDoc={srcDoc}
            className="h-[70vh] min-h-[420px] w-full border-none bg-white"
          />
        ) : hasText ? (
          <pre className="max-h-[70vh] w-full overflow-auto whitespace-pre-wrap break-words p-6 text-body font-body text-default-font">
            {message.body_text}
          </pre>
        ) : (
          <span className="p-6 text-body font-body text-subtext-color">
            This mail has no text or HTML body — it may be attachment-only.
          </span>
        )}
      </div>
    </div>
  );
};

export default MessageViewer;
